import React from 'react';

import { TIMELINE_CONFIG } from '../constants';
import type { ComputedJoint, EditedSegment } from '../utils/cuts-to-segments';
import { TimelineJoint } from './timeline-joint';
import { TimelineRuler } from './timeline-ruler';
import { TimelineSegment } from './timeline-segment';

type TimelineLayoutProps = {
  timelineViewportRef: React.RefObject<HTMLDivElement>;
  timelineWidthPx: number;
  outputDurationSec: number;
  zoom: number;
  pixelsPerSecond: number;
};

type TimelineSegmentsProps = {
  activeSegments: EditedSegment[];
  interactiveJoints: ComputedJoint[];
};

type TimelineSelectionProps = {
  selectedSegmentId: string | null;
  selectedJointId: string | null;
};

type TimelineDragProps = {
  draggingSegmentId: string | null;
  draggingHandle: 'start' | 'end' | null;
  previewTime: number | null;
};

type TimelineHandlersProps = {
  onSeek: (timeSec: number) => void;
  onSelectSegment: (segmentId: string) => void;
  onSelectJoint: (jointId: string) => void;
  onRemoveCut: (jointId: string) => void;
  onDeleteSegment: (segmentId: string) => void;
  onSegmentClick: (segment: EditedSegment, clientX: number) => void;
  onTrimHandleMouseDown: (
    segment: EditedSegment,
    handle: 'start' | 'end',
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void;
};

type AdvancedTimelineTrackProps = {
  layout: TimelineLayoutProps;
  segments: TimelineSegmentsProps;
  selection: TimelineSelectionProps;
  drag: TimelineDragProps;
  handlers: TimelineHandlersProps;
  canEdit: boolean;
  /**
   * When true the edit is frozen (PROCESSING or COMPLETED).
   * Joint selection is suppressed so the restore-segment menu never opens,
   * and trim handles are hidden in TimelineSegment.
   */
  isFrozen?: boolean;
  children?: React.ReactNode;
};

export const AdvancedTimelineTrack = ({
  layout,
  segments,
  selection,
  drag,
  handlers,
  canEdit,
  isFrozen = false,
  children,
}: AdvancedTimelineTrackProps): React.JSX.Element => {
  const {
    timelineViewportRef,
    timelineWidthPx,
    outputDurationSec,
    zoom,
    pixelsPerSecond,
  } = layout;
  const { activeSegments, interactiveJoints } = segments;
  const { selectedSegmentId, selectedJointId } = selection;
  const { draggingSegmentId, draggingHandle, previewTime } = drag;
  const {
    onSeek,
    onSelectSegment,
    onSelectJoint,
    onRemoveCut,
    onDeleteSegment,
    onSegmentClick,
    onTrimHandleMouseDown,
  } = handlers;
  const canDeleteSegment = activeSegments.length > 1;
  const jointsByBeforeSegmentId = new Map<string, ComputedJoint>();
  interactiveJoints.forEach(joint => {
    if (!joint.beforeSegmentId) {
      return;
    }
    jointsByBeforeSegmentId.set(joint.beforeSegmentId, joint);
  });

  return (
    <div
      ref={timelineViewportRef}
      className="relative overflow-x-auto overflow-y-hidden rounded border border-spring-neutral-b0/15 [scrollbar-width:thin] [scrollbar-color:#696e7c_transparent]"
    >
      <div
        className="relative overflow-x-hidden overflow-y-visible"
        style={{ width: `${Math.max(1, timelineWidthPx)}px` }}
      >
        <TimelineRuler
          durationSec={outputDurationSec}
          zoom={zoom}
          pixelsPerSecond={pixelsPerSecond}
          onSeek={onSeek}
        />

        <div
          className="relative"
          style={{ height: `${TIMELINE_CONFIG.trackHeight}px` }}
        >
          <div className="flex h-full items-stretch">
            {activeSegments.map(segment => {
              const joint = jointsByBeforeSegmentId.get(segment.id);
              return (
                <React.Fragment key={segment.id}>
                  <TimelineSegment
                    segment={segment}
                    zoom={zoom}
                    pixelsPerSecond={pixelsPerSecond}
                    isFrozen={isFrozen}
                    isSelected={selectedSegmentId === segment.id}
                    canDelete={canDeleteSegment}
                    dragPreview={
                      draggingSegmentId === segment.id && previewTime != null
                        ? draggingHandle === 'start'
                          ? { startMs: previewTime }
                          : { endMs: previewTime }
                        : undefined
                    }
                    onSelect={segmentId => {
                      onSelectSegment(segmentId);
                    }}
                    onClick={(_segmentId, clientX) => {
                      onSegmentClick(segment, clientX);
                    }}
                    onDeleteSegment={segmentId => {
                      onDeleteSegment(segmentId);
                    }}
                    onTrimHandleMouseDown={(_segmentId, handle, event) => {
                      if (!canEdit) {
                        return;
                      }
                      onTrimHandleMouseDown(segment, handle, event);
                    }}
                  />
                  {joint ? (
                    <div className="relative h-full w-0 shrink-0 overflow-visible z-20">
                      <div className="absolute top-1/2 -translate-y-1/2">
                        <TimelineJoint
                          joint={joint}
                          isSelected={selectedJointId === joint.id}
                          onClick={jointId => {
                            // Don't open the restore-segment menu when frozen —
                            // the action would be a silent no-op and the open
                            // menu would mislead the user.
                            if (isFrozen) return;
                            onSelectJoint(jointId);
                          }}
                          onRemoveCut={onRemoveCut}
                        />
                      </div>
                    </div>
                  ) : null}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};
